import { DEALS_ENABLED } from '@forecastify/shared';
import { useState, type DragEvent, type ReactNode } from 'react';
import { Button, Icon, Select, type IconName } from '@forecastify/ui';
import { useT, type MessageKey, type Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The workspace-settings shell — `markup.html` 1712–1748.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A 216px section rail on the left, the section body on the right, and a `Select` instead of the
 * rail below `md`. Every number is the mockup's.
 *
 * ⚠️⚠️ **EACH SECTION IS ITS OWN ROUTE, WHERE THE MOCKUP KEEPS ONE `view` AND A `section` VARIABLE.**
 * R-8 chose real paths, and the consequence lands here: `/settings/general` and `/settings/worklog`
 * are addresses somebody can bookmark, send to a colleague and land on — which a component-state
 * section cannot be. It also means the reachability suite audits all seven, rather than one screen
 * that happens to render seven bodies.
 *
 * ⚠️ **The rail lists only the sections the caller may see**, which is the mockup's own
 * `NAV.filter(wsCan)` — it hides a section rather than showing a denied panel. Its *"Nincs
 * jogosultságod"* body still exists for the case somebody types the address directly, because a rail
 * that hides a link is UX and the server check is the security.
 *
 * ⚠️ **The mockup's *"Nézet mint"* role simulator is NOT built.** It is a prototype affordance for
 * demoing three roles from one browser; ours has real roles and a real permission set, and a control
 * that pretended to change them would be showing a fiction of the server's answer.
 */

export interface SettingsSection {
  /** The path segment, and the marker's suffix. */
  readonly key: string;
  readonly label: MessageKey;
  readonly icon: IconName;
  /** The action that has to be held for the section to appear in the rail. */
  readonly requires: string;
}

/**
 * The mockup's `NAV`, in its order.
 *
 * ⚠️ **`perms` and `fields` are C5's and C10's screens**, which shipped as standalone routes before
 * a shell existed. They are the same two sections the mockup lists, so they join the rail rather than
 * staying beside it — the drift `app.tsx` warns about, in the one place it would have been invisible.
 */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  { key: 'general', label: 'settings.nav.general', icon: 'settings', requires: 'settings.view' },
  {
    key: 'permissions',
    label: 'settings.nav.permissions',
    icon: 'shield-check',
    /* ⚠️ The role matrix is `settings.manage`'s, not `settings.view`'s — reading who may do what is
     * itself an administrative act, and C5's screen already checks it. */
    requires: 'settings.manage',
  },
  { key: 'tasks', label: 'settings.nav.tasks', icon: 'list-checks', requires: 'settings.view' },
  /**
   * C42 · the workspace calendar.
   *
   * ⚠️⚠️ **THE MOCKUP PUTS IT BETWEEN `perms` AND `fields`, AND THAT IS WHERE IT GOES HERE TOO** even
   * though our rail is not in the mockup's order overall: ours moved `permissions` to second place
   * long before Phase 3. What the mockup's placement says is that the calendar belongs with the
   * VOCABULARIES — the lists a workspace curates — rather than with the rules, and *before* the
   * metafields is the position that keeps that true in both orders.
   *
   * ⚠️ `settings.view` to look, `settings.manage` to change. The screen hides the import button and
   * the row menus for a viewer; the server checks again either way.
   */
  /**
   * C43 · work schedules.
   *
   * ⚠️ **Beside the calendar, and before it in the mockup's order.** `NAV` there reads
   * `work, leave, overtime, perms, holidays` — the working-time sections sit together, and the two
   * that exist so far keep that adjacency even though the three between them are not built yet.
   */
  /**
   * C54 · ⚠️⚠️ **IMMEDIATELY BEFORE THE SCHEDULES, WHICH IS THE ONE ADJACENCY OF THE
   * MOCKUP'S THAT SURVIVES OUR OWN RAIL ORDER.** Its `NAV` reads
   * `general, tasks, proles, skills, work, rates, …` — so skills sits between *Projekt szerepek*
   * and *Munkarendek*. We put `project-roles` far lower on purpose (see its own note), so the
   * first half of that sandwich does not exist here; the second does, and this keeps it.
   *
   * ⚠️ **`PHASE-5.md` §1 SAYS *"between Díjkártyák and Munkarendek"* AND IS WRONG** — read
   * off the rendered screenshot rather than off `NAV`. Corrected there too.
   */
  { key: 'skills', label: 'settings.nav.skills', icon: 'sparkles', requires: 'settings.view' },
  {
    key: 'schedules',
    label: 'settings.nav.schedules',
    icon: 'calendar-days',
    requires: 'settings.view',
  },
  /**
   * C51 · ⚠️ **Between the schedules and the leave rules, which is the mockup's own `NAV` order** —
   * `work, rates, currency, leave`. The two money sections sit with the other things that describe
   * a person's working arrangement rather than beside the integrations.
   */
  { key: 'rates', label: 'settings.nav.rates', icon: 'credit-card', requires: 'settings.view' },
  { key: 'currency', label: 'settings.nav.currency', icon: 'globe', requires: 'settings.view' },
  { key: 'leave', label: 'settings.nav.leave', icon: 'sun', requires: 'settings.view' },
  { key: 'overtime', label: 'settings.nav.overtime', icon: 'timer', requires: 'settings.view' },
  { key: 'holidays', label: 'settings.nav.holidays', icon: 'calendar', requires: 'settings.view' },
  { key: 'metafields', label: 'settings.nav.metafields', icon: 'tag', requires: 'settings.view' },
  /**
   * C31 · ⚠️ **Five rows below `permissions`, and deliberately not beside it.** That one decides
   * what somebody MAY DO; this one names what somebody DOES. The mockup puts *Projekt szerepek*
   * here too, among the vocabularies.
   */
  {
    key: 'project-roles',
    label: 'settings.nav.projectRoles',
    icon: 'user-round',
    requires: 'settings.view',
  },
  /* R-60 · the teams people are grouped and filtered by. Beside the other people vocabulary. */
  { key: 'teams', label: 'settings.nav.teams', icon: 'users', requires: 'settings.view' },
  /* Phase 6 · after *Projekt szerepek*, where the mockup puts it — a placeholder profile is a
     role at a seniority, so the two vocabularies sit together. */
  /* 21 Oct 2026 · off until deal management is finished — `DEALS_ENABLED`. */
  ...(DEALS_ENABLED
    ? [
        {
          key: 'deals',
          label: 'settings.nav.deals',
          icon: 'tag',
          requires: 'settings.view',
        } as const,
      ]
    : []),
  { key: 'worklog', label: 'settings.nav.worklog', icon: 'clock', requires: 'settings.view' },
  {
    key: 'integrations',
    label: 'settings.nav.integrations',
    icon: 'plug',
    requires: 'settings.view',
  },
  { key: 'data', label: 'settings.nav.data', icon: 'database', requires: 'settings.manage' },
  /**
   * ux-pass-2 · ⚠️ **`audit.view`, which only the built-in admin role holds — U-8.** After *Adatok*,
   * with the other administrative sections: it is a record of the workspace rather than a setting
   * of it, and the rail hides it from everybody the server would refuse.
   */
  { key: 'audit', label: 'settings.nav.audit', icon: 'file-text', requires: 'audit.view' },
  /**
   * ⚠️⚠️ **IN THE RAIL, WHERE THE MOCKUP KEEPS BILLING OUTSIDE IT.** Its billing is a separate
   * `view` reached from the user menu, and its `NAV` does not list it — but its own `wsCan` treats
   * `billing` as a section with a role check (owner only), so the exclusion is about where the menu
   * item sits rather than about what billing is. We have no equivalent menu, and inventing one to
   * hold a single entry would be a second navigation for one screen. `billing.manage` gates it, so
   * the rail shows it to exactly the role the mockup's own check names.
   */
  {
    key: 'billing',
    label: 'settings.nav.billing',
    icon: 'credit-card',
    requires: 'billing.manage',
  },
];

/**
 * Where a section key lives.
 *
 * ⚠️⚠️ **`general` IS `/settings`, and this is not cosmetic.** A `/settings` that only redirected
 * rendered no screen of its own, so the reachability suite's *"every route renders its own screen"*
 * case had nothing to assert — it failed with `expected null not to be null`, correctly. A route
 * that exists to bounce is a route the gate cannot check, so the default section simply lives at the
 * default address.
 */
export function sectionPath(key: string): string {
  /**
   * ⚠️⚠️ **THE MARKETPLACE ROW IS GONE, AND WITH IT THE ONE EXCEPTION THIS FUNCTION HAD — item
   * 68.** It was the last row of the nav and the only one that left the shell: `sectionPath` sent
   * it to `/marketplace`, a `chrome: 'bare'` page whose rail was not on screen once you arrived.
   * The install it existed for is a switch on the Integrációk card now (D7), so every remaining
   * row is a section of these settings and this is a plain mapping again.
   */
  return key === 'general' ? '/settings' : `/settings/${key}`;
}

export function sectionLabel(t: Translate, key: string): string {
  const section = SETTINGS_SECTIONS.find((candidate) => candidate.key === key);
  return section === undefined ? '' : t(section.label);
}

export function SettingsShell({
  section,
  permissions,
  onNavigate,
  children,
}: {
  readonly section: string;
  /**
   * The caller's effective actions.
   *
   * ⚠️ **`{ has }` rather than a `ReadonlySet`**, because that is what `usePermissionSet` returns —
   * a scope-aware object, not a set of strings. Typing this as a Set compiled only because `has`
   * happens to match, and would have broken the day the shell wanted `scopeOf`.
   */
  readonly permissions: { readonly has: (action: string) => boolean };
  readonly onNavigate: (key: string) => void;
  readonly children: ReactNode;
}) {
  const t = useT();
  const visible = SETTINGS_SECTIONS.filter((entry) => permissions.has(entry.requires));

  return (
    <div className="flex min-h-0 flex-1 flex-col md:flex-row">
      {/**
       * ⚠️⚠️ **NO RAIL OF ITS OWN SINCE 13 Oct 2026 — the shell draws the settings menu.** Since
       * 14 Oct it is the settings panel beside the narrowed icon rail (`app.tsx`, settings mode;
       * `SettingsPanel`), with these sections, the person's own, a settings-only search and a back
       * arrow. A second 218px rail here would be the same list twice. The narrow picker below
       * stays for the width where the sidebar is a drawer and the panel is not drawn.
       */}

      {/* ⚠️ Below `md` the rail becomes a `Select` — the mockup's own fallback, and the native
          control for the reason `select.tsx` records. */}
      <div className="flex items-center gap-2 border-b p-2 desktop:hidden">
        <Select
          value={section}
          onChange={(event) => {
            onNavigate(event.target.value);
          }}
          options={visible.map((entry) => ({
            value: entry.key,
            label: t(entry.label),
            /* 17 Oct 2026 · the rail's own icon, on the phone's menu too. */
            icon: entry.icon,
          }))}
          aria-label={t('settings.nav.label')}
          className="w-full"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}

/**
 * The mockup's *"Nincs jogosultságod"* body.
 *
 * ⚠️ **Reachable only by typing the address**, because the rail hides what the caller cannot hold —
 * which is exactly why it has to exist: a hidden link is UX, and somebody with an old bookmark still
 * arrives. Not an error state: nothing failed, and `state-rules.md` keeps the two apart.
 */
export function SettingsDenied() {
  const t = useT();

  return (
    <div className="mx-auto mt-10 flex max-w-[420px] flex-col items-center gap-2 px-5 text-center">
      <span className="bg-muted grid size-12 place-items-center rounded-xl">
        <Icon name="shield-check" size={22} className="text-muted-foreground" />
      </span>
      <div className="mt-1 text-[14px] font-medium">{t('settings.denied.title')}</div>
      <div className="text-muted-foreground text-[12.5px] leading-relaxed">
        {t('settings.denied.body')}
      </div>
    </div>
  );
}

/** One card of a section — the mockup's `rounded-xl border p-4 shadow-sm` with a title and a lede. */
export function SettingsCard({
  title,
  lede,
  children,
  className,
  tourId,
}: {
  readonly title: string;
  readonly lede?: string;
  readonly children: ReactNode;
  readonly className?: string;
  /** A stable hook for the guided tour (`data-tour`) — F-2. */
  readonly tourId?: string;
}) {
  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C66 · THE TITLE SITS IN A BORDERED HEADER, AND THAT IS THE DIVIDER.      │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * *"Better dividers between sections"* — the form screens were a stack of cards whose headings
   * floated above their fields with nothing between them, so a long card read as two cards and two
   * short ones read as one. The workspace-settings mockup rules a line under every card heading
   * (`border-b px-4 py-3`), and that one rule is the whole difference.
   */
  return (
    <section
      aria-label={title}
      data-tour={tourId}
      className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm"
    >
      <div className="border-b px-4 py-3">
        <h3 className="text-[13px] font-medium">{title}</h3>
        {lede !== undefined && (
          <p className="text-muted-foreground mt-0.5 text-[11.5px] leading-relaxed">{lede}</p>
        )}
      </div>
      {/* ⚠️ A caller passing `className` is wrapping a TABLE — it wants the body flush to the
          card's edges, so the padding is the caller's to set. */}
      <div className={className ?? 'p-4'}>{children}</div>
    </section>
  );
}

/**
 * The Save / Elvetés footer every editable section shares.
 *
 * ⚠️ **Elvetés is disabled when there is nothing to discard, and Save when there is nothing to
 * save** — the mockup's `wsGeneralClean`. Two buttons that are always live make "did that do
 * anything?" a question somebody has to answer by looking.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ DRAG A ROW BY ITS GRIP. One implementation, three settings tables.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Asked for on 15 Sep: *"this has a weird chevron sorting, whereas you built a proper grabber based
 * one… we need that grabber for the tasks statuses/priorities too."* The leave types had it inline,
 * the overtime types had a copy, and statuses and priorities had a pair of chevrons — three answers
 * to one question, which is how they come to disagree about what a stale drag does.
 *
 * ⚠️⚠️ **THE WHOLE SET IN ITS NEW ORDER, NEVER A `{ moved, before }` PAIR.** The server refuses
 * anything that is not a permutation of what it holds, which is what makes a drag against a stale
 * list a refusal rather than a partial write.
 *
 * ⚠️⚠️ **AND A KEYBOARD PATH IS STILL REQUIRED, WHICH IS WHY `moveTo` IS RETURNED.** No key event
 * starts an HTML5 drag, so a grabber alone is a control a keyboard user cannot reach at all — the
 * chevrons this replaces existed for exactly that reason and were right about it. Every caller puts
 * *Feljebb* and *Lejjebb* in the row menu, which is the same write by a reachable route. `board.tsx`
 * records the identical rule for the kanban.
 *
 * ⚠️ **`dragleave` is not listened to.** It fires when the pointer crosses onto a CHILD of the row,
 * so a row containing a badge de-highlights itself as the pointer passes over it —
 * `board.tsx` carries the long version, found with a screen recording.
 */
export function useDragOrder(
  ids: readonly string[],
  onReorder: ((ids: readonly string[]) => void) | undefined,
): {
  readonly enabled: boolean;
  readonly dragging: string | null;
  readonly over: string | null;
  readonly rowProps: (id: string) => Record<string, unknown>;
  readonly moveTo: (id: string, to: number) => void;
} {
  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const reorder = (held: string, target: string): void => {
    if (held === target) return;
    const next = [...ids];
    const from = next.indexOf(held);
    if (from < 0) return;
    next.splice(from, 1);
    next.splice(next.indexOf(target), 0, held);
    onReorder?.(next);
  };

  return {
    enabled: onReorder !== undefined,
    dragging,
    over,
    rowProps: (id) =>
      onReorder === undefined
        ? {}
        : {
            draggable: true,
            onDragStart: () => {
              setDragging(id);
            },
            onDragOver: (event: DragEvent) => {
              event.preventDefault();
              if (over !== id) setOver(id);
            },
            onDragEnd: () => {
              setDragging(null);
              setOver(null);
            },
            onDrop: (event: DragEvent) => {
              event.preventDefault();
              const held = dragging;
              setDragging(null);
              setOver(null);
              if (held !== null) reorder(held, id);
            },
          },
    moveTo: (id, to) => {
      const next = [...ids];
      const from = next.indexOf(id);
      if (from < 0 || to < 0 || to >= next.length) return;
      next.splice(from, 1);
      next.splice(to, 0, id);
      onReorder?.(next);
    },
  };
}

/** The handle itself. ⚠️ `aria-hidden`: the keyboard path is the row menu, not this. */
export function Grip() {
  return (
    <span aria-hidden className="text-muted-foreground flex w-3.5 shrink-0 cursor-grab">
      <Icon name="grip-vertical" size={14} />
    </span>
  );
}

export function SettingsFooter({
  note,
  dirty,
  saving,
  onReset,
  onSave,
}: {
  readonly note: string;
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly onReset: () => void;
  readonly onSave: () => void;
}) {
  const t = useT();

  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground text-[12px]">{note}</span>
      <div className="flex-1" />
      {/* ⚠️ `Button`, not hand-rolled classes — a screen composes components (CLAUDE.md §3). */}
      <Button variant="outline" disabled={!dirty || saving} onClick={onReset}>
        {t('common.discard')}
      </Button>
      <Button disabled={!dirty || saving} onClick={onSave}>
        {saving ? t('common.saving') : t('common.save')}
      </Button>
    </div>
  );
}
